// ═══════════════════════════════════════════════════════════════
// Cognitive Engine — the honest calculator behind the Session Read
// (per match) and the Cognitive Atlas (accumulated across matches).
//
// Design: docs/wot_cognitive_atlas_design.md
// Principles (non-negotiable):
//   1. Nothing unobserved is ever scored ("not yet observed" > invented).
//   2. Confidence maps to visual clarity, never to hidden multipliers.
//   3. A single match never carries an identity claim.
//   4. Vocabulary: knowledge / pace / pattern. Never "intelligence" or "IQ".
//
// Pure functions only — no store, no network. Deterministic and testable.
// ═══════════════════════════════════════════════════════════════

export type Difficulty = 'easy' | 'medium' | 'hard';

export interface AnswerEvent {
  realm: string;            // uppercase realm id, e.g. 'EUREKA'
  difficulty: Difficulty;
  correct: boolean;
  responseTimeMs: number;   // clamped 0..MAX_CLOCK_MS by the caller
  roundIndex: number;       // 1-based position within the match
  timedOut?: boolean;       // ran out of time (counts as wrong, excluded from pace)
  matchDate?: string;       // ISO date, used only by the Atlas recency weight
}

// ── Regions: 10 realms -> 5 knowledge regions ───────────────────
export const REGIONS: Record<string, { name: string; realms: string[] }> = {
  SCIENCES:   { name: 'Sciences',   realms: ['EUREKA', 'COSMOS', 'NEXUS'] },
  HUMANITIES: { name: 'Humanities', realms: ['CHRONICLES', 'ATLAS'] },
  CULTURE:    { name: 'Culture',    realms: ['SPOTLIGHT', 'SOUNDWAVE'] },
  LIFESTYLE:  { name: 'Lifestyle',  realms: ['FEAST', 'ARENA'] },
  ABSTRACT:   { name: 'Abstract',   realms: ['ENIGMA'] },
};

export function regionOfRealm(realm: string): string | null {
  const r = realm.toUpperCase();
  for (const [key, region] of Object.entries(REGIONS)) {
    if (region.realms.includes(r)) return key;
  }
  return null;
}

// ── Elo core (knowledge estimate per realm/region) ──────────────
// Each answer is a "match" against the question tier. Getting a hard
// question right moves you a lot; missing an easy one costs a lot;
// getting an easy one right barely moves you. This is the principled
// version of "different weights for easy/medium/hard".
export const ELO_START = 1000;
const QUESTION_RATING: Record<Difficulty, number> = { easy: 850, medium: 1050, hard: 1300 };

export function eloExpected(playerR: number, difficulty: Difficulty): number {
  const q = QUESTION_RATING[difficulty];
  return 1 / (1 + Math.pow(10, (q - playerR) / 400));
}

/** K shrinks as observations accumulate: early answers sketch, later ones refine. */
export function eloK(nObserved: number): number {
  return 32 / (1 + nObserved / 40);
}

export function eloUpdate(playerR: number, difficulty: Difficulty, correct: boolean, nObserved: number): number {
  const e = eloExpected(playerR, difficulty);
  return playerR + eloK(nObserved) * ((correct ? 1 : 0) - e);
}

// ── Pace (retrieval fluency) ────────────────────────────────────
// Median latency of CORRECT answers only (wrong answers carry guess noise),
// normalized by difficulty so hard questions are allowed to take longer.
const LATENCY_BASELINE_MS: Record<Difficulty, number> = { easy: 4500, medium: 6000, hard: 7500 };

export function median(xs: number[]): number {
  if (xs.length === 0) return NaN;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export type PaceBand = 'Instant' | 'Swift' | 'Measured' | 'Deliberate' | 'Unhurried';

export function paceBand(ratio: number): PaceBand {
  if (ratio < 0.55) return 'Instant';
  if (ratio < 0.8) return 'Swift';
  if (ratio < 1.15) return 'Measured';
  if (ratio < 1.5) return 'Deliberate';
  return 'Unhurried';
}

/** ratio = observed median / difficulty-expected median. <1 is faster than typical. */
export function computePace(events: AnswerEvent[]): { ratio: number; band: PaceBand; medianMs: number; n: number } | null {
  const correct = events.filter(e => e.correct && !e.timedOut);
  if (correct.length < 3) return null; // below this, pace is noise
  const normalized = correct.map(e => e.responseTimeMs / LATENCY_BASELINE_MS[e.difficulty]);
  const ratio = median(normalized);
  return { ratio, band: paceBand(ratio), medianMs: Math.round(median(correct.map(e => e.responseTimeMs))), n: correct.length };
}

// ── Steadiness (real sequence, replaces the fabricated streak) ──
// Two observed components:
//   tempo: coefficient of variation of correct-answer latencies (low = metronome)
//   rhythm: runs of correct/wrong vs. the count expected by chance at the same
//           accuracy (clustered errors = streaky, well-mixed = steady)
export type SteadinessBand = 'Metronomic' | 'Steady' | 'Wavering' | 'Streaky' | 'Turbulent';

export function steadinessBand(score: number): SteadinessBand {
  if (score >= 85) return 'Metronomic';
  if (score >= 70) return 'Steady';
  if (score >= 50) return 'Wavering';
  if (score >= 30) return 'Streaky';
  return 'Turbulent';
}

export function computeSteadiness(events: AnswerEvent[]): { score: number; band: SteadinessBand; n: number } | null {
  if (events.length < 6) return null;

  let score = 100;

  // tempo component (needs >=4 correct latencies to say anything)
  const lats = events.filter(e => e.correct && !e.timedOut).map(e => e.responseTimeMs);
  if (lats.length >= 4) {
    const mean = lats.reduce((a, b) => a + b, 0) / lats.length;
    const sd = Math.sqrt(lats.reduce((a, b) => a + (b - mean) ** 2, 0) / lats.length);
    const cv = mean > 0 ? sd / mean : 0;
    score -= Math.min(60, cv * 120);
  }

  // rhythm component (needs both outcomes present)
  const w = events.filter(e => !e.correct).length;
  const c = events.length - w;
  if (w > 0 && c > 0) {
    let runs = 1;
    for (let i = 1; i < events.length; i++) {
      if (events[i].correct !== events[i - 1].correct) runs++;
    }
    const expected = 1 + (2 * w * c) / events.length;
    const deviation = Math.abs(runs - expected) / expected;
    score -= Math.min(30, deviation * 40);
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  return { score, band: steadinessBand(score), n: events.length };
}

// ── Resilience (bounce-back after a miss) ───────────────────────
export function computeResilience(events: AnswerEvent[]): { recovered: number; observed: number; rate: number } | null {
  let observed = 0;
  let recovered = 0;
  for (let i = 0; i < events.length - 1; i++) {
    if (!events[i].correct) {
      observed++;
      if (events[i + 1].correct) recovered++;
    }
  }
  if (observed < 2) return null; // one miss is an anecdote, not a pattern
  return { recovered, observed, rate: recovered / observed };
}

// ── Clutch (accuracy under time pressure) ───────────────────────
export function computeClutch(events: AnswerEvent[]): { lateAccuracy: number; earlyAccuracy: number; nLate: number } | null {
  const late = events.filter(e => e.responseTimeMs >= 10000 && !e.timedOut);
  const early = events.filter(e => e.responseTimeMs < 10000 && !e.timedOut);
  if (late.length < 3 || early.length < 3) return null;
  return {
    lateAccuracy: late.filter(e => e.correct).length / late.length,
    earlyAccuracy: early.filter(e => e.correct).length / early.length,
    nLate: late.length,
  };
}

// ── Real streak (longest run of corrects — the honest tile) ─────
export function longestCorrectRun(events: AnswerEvent[]): number {
  let best = 0;
  let cur = 0;
  for (const e of events) {
    cur = e.correct ? cur + 1 : 0;
    if (cur > best) best = cur;
  }
  return best;
}

// ── Session Read (single match — weather, not climate) ──────────
export interface SessionRead {
  n: number;
  correct: number;
  accuracy: number;
  bestStreak: number;
  pace: ReturnType<typeof computePace>;
  steadiness: ReturnType<typeof computeSteadiness>;
  resilience: ReturnType<typeof computeResilience>;
  clutch: ReturnType<typeof computeClutch>;
  sharpestRegion: { region: string; name: string; correct: number; n: number } | null;
  timeline: AnswerEvent[];
}

export function computeSessionRead(events: AnswerEvent[]): SessionRead {
  const n = events.length;
  const correct = events.filter(e => e.correct).length;

  // sharpest region: needs >=3 answers in that region and >1 region present
  let sharpestRegion: SessionRead['sharpestRegion'] = null;
  const byRegion = new Map<string, { c: number; n: number }>();
  for (const e of events) {
    const reg = regionOfRealm(e.realm);
    if (!reg) continue;
    const cur = byRegion.get(reg) || { c: 0, n: 0 };
    cur.n++;
    if (e.correct) cur.c++;
    byRegion.set(reg, cur);
  }
  if (byRegion.size > 1) {
    let best: { region: string; c: number; n: number } | null = null;
    for (const [region, { c, n: rn }] of byRegion) {
      if (rn < 3) continue;
      if (!best || c / rn > best.c / best.n || (c / rn === best.c / best.n && rn > best.n)) {
        best = { region, c, n: rn };
      }
    }
    if (best && best.c > 0) {
      sharpestRegion = { region: best.region, name: REGIONS[best.region].name, correct: best.c, n: best.n };
    }
  }

  return {
    n,
    correct,
    accuracy: n > 0 ? correct / n : 0,
    bestStreak: longestCorrectRun(events),
    pace: computePace(events),
    steadiness: computeSteadiness(events),
    resilience: computeResilience(events),
    clutch: computeClutch(events),
    sharpestRegion,
    timeline: events,
  };
}

// ── Atlas (accumulated — climate; consumed by Phase C UI) ───────
export type ConfidenceTier = 'HIDDEN' | 'FAINT' | 'CHARTED';

export function confidenceTier(n: number, n1: number, n2: number): ConfidenceTier {
  if (n < n1) return 'HIDDEN';
  if (n < n2) return 'FAINT';
  return 'CHARTED';
}

export interface RegionEstimate {
  region: string;
  name: string;
  elo: number;
  n: number;
  tier: ConfidenceTier; // n1=10, n2=30 answers per region
}

export interface Atlas {
  regions: RegionEstimate[];
  globalElo: number | null;       // median of FAINT+CHARTED regions
  pace: (NonNullable<ReturnType<typeof computePace>> & { tier: ConfidenceTier }) | null;       // n1=8, n2=25
  steadiness: (NonNullable<ReturnType<typeof computeSteadiness>> & { tier: ConfidenceTier }) | null; // n1=15, n2=40
  resilience: (NonNullable<ReturnType<typeof computeResilience>> & { tier: ConfidenceTier }) | null; // n1=5 misses
  totalAnswers: number;
}

/** Recency: answers older than ~90 days fade (half-life). Applied as an
 *  observation-count discount so old games stop dominating the estimates. */
export function recencyWeight(matchDate: string | undefined, now: Date): number {
  if (!matchDate) return 1;
  const days = Math.max(0, (now.getTime() - new Date(matchDate).getTime()) / 86400000);
  return Math.pow(0.5, days / 90);
}

// ── Knowledge bands: the Elo is an internal estimate, not a player-facing
// number. "1048" tells a player nothing; a word does. Bands are centered on
// the 1000 start so an untested region reads as neutral, not as failure.
export type KnowledgeBand = 'Commanding' | 'Strong' | 'Steady' | 'Finding footing' | 'Rough ground';

export function knowledgeBand(elo: number): KnowledgeBand {
  if (elo >= 1180) return 'Commanding';
  if (elo >= 1070) return 'Strong';
  if (elo >= 970) return 'Steady';
  if (elo >= 880) return 'Finding footing';
  return 'Rough ground';
}

/** 0..1 fill for a region bar, same scale the map arcs use. */
export function knowledgeFraction(elo: number): number {
  return Math.max(0.06, Math.min(1, (elo - 800) / 500));
}

// ── Archetype v2: earned identity (Atlas-only, never from one match) ─
// Vocabulary is exploration/chart language on purpose: it does NOT collide
// with PsycheMatrix's own archetype taxonomy (Strategist/Innovator/etc.),
// so the funnel can honestly say "the full map lives over there".
export interface AtlasArchetype {
  key: string;
  name: string;        // e.g. "THE CARTOGRAPHER"
  line: string;        // one-sentence signature
  leaning: boolean;    // true while the map is young (30..59 answers)
}

const ARCHETYPES: Record<string, { name: string; line: string }> = {
  PROSPECTOR:  { name: 'THE PROSPECTOR',  line: 'Digs where the ground is hardest, and finds gold.' },
  ECHO:        { name: 'THE ECHO',        line: 'Every miss comes back as a sharper answer.' },
  COMET:       { name: 'THE COMET',       line: 'Blazing pace, a tail of turbulence.' },
  DEEPCURRENT: { name: 'THE DEEP CURRENT', line: 'Unhurried on the surface, powerful underneath.' },
  LIGHTHOUSE:  { name: 'THE LIGHTHOUSE',  line: 'One region shines far brighter than the rest.' },
  CARTOGRAPHER:{ name: 'THE CARTOGRAPHER', line: 'Charts wide, steady lines across the map.' },
  WILDFIRE:    { name: 'THE WILDFIRE',    line: 'Burns in streaks, strongest under pressure.' },
  DRIFTER:     { name: 'THE DRIFTER',     line: 'Still finding a heading. The map is young.' },
};

/** Difficulty appetite: accuracy on hard vs. overall (needs real hard volume). */
export function hardAffinity(events: AnswerEvent[]): { delta: number; nHard: number } | null {
  const hard = events.filter(e => e.difficulty === 'hard');
  if (hard.length < 12 || hard.length === events.length) return null;
  const hardAcc = hard.filter(e => e.correct).length / hard.length;
  const overallAcc = events.filter(e => e.correct).length / events.length;
  return { delta: hardAcc - overallAcc, nHard: hard.length };
}

export function computeArchetype(atlas: Atlas, events: AnswerEvent[]): AtlasArchetype {
  const n = atlas.totalAnswers;
  if (n < 30) return { key: 'DRIFTER', ...ARCHETYPES.DRIFTER, leaning: false };

  const leaning = n < 60;
  const visible = atlas.regions.filter(r => r.tier !== 'HIDDEN');
  const peak = visible.length > 0 ? visible[0] : null; // regions sorted desc
  const elos = visible.map(r => r.elo);
  const med = elos.length > 0 ? median(elos) : ELO_START;

  const fast = atlas.pace && (atlas.pace.band === 'Instant' || atlas.pace.band === 'Swift');
  const slow = atlas.pace && (atlas.pace.band === 'Deliberate' || atlas.pace.band === 'Unhurried');
  const steady = atlas.steadiness && atlas.steadiness.score >= 70;
  const volatile = atlas.steadiness && atlas.steadiness.score < 50;
  const affinity = hardAffinity(events);
  const clutch = computeClutch(events);

  const pick = (key: string): AtlasArchetype => ({ key, ...ARCHETYPES[key], leaning });

  // Most distinctive signatures first; DRIFTER is the honest fallback.
  if (affinity && affinity.delta >= 0.08) return pick('PROSPECTOR');
  if (atlas.resilience && atlas.resilience.tier !== 'HIDDEN' && atlas.resilience.rate >= 0.65) return pick('ECHO');
  if (fast && volatile) return pick('COMET');
  if (slow && steady && peak && peak.elo >= 1080) return pick('DEEPCURRENT');
  if (peak && visible.length >= 2 && peak.elo - med >= 110) return pick('LIGHTHOUSE');
  if (visible.length >= 3 && steady) return pick('CARTOGRAPHER');
  if (volatile && clutch && clutch.lateAccuracy > clutch.earlyAccuracy + 0.1) return pick('WILDFIRE');
  return pick('DRIFTER');
}

export function computeAtlas(events: AnswerEvent[], now: Date = new Date()): Atlas {
  // per-realm elo, chronological
  const realmState = new Map<string, { r: number; n: number }>();
  for (const e of events) {
    const key = e.realm.toUpperCase();
    const st = realmState.get(key) || { r: ELO_START, n: 0 };
    st.r = eloUpdate(st.r, e.difficulty, e.correct, st.n);
    st.n += recencyWeight(e.matchDate, now);
    realmState.set(key, st);
  }

  // roll realms up into regions (n-weighted elo)
  const regions: RegionEstimate[] = [];
  for (const [key, region] of Object.entries(REGIONS)) {
    let totalN = 0;
    let weighted = 0;
    for (const realm of region.realms) {
      const st = realmState.get(realm);
      if (!st) continue;
      totalN += st.n;
      weighted += st.r * st.n;
    }
    if (totalN > 0) {
      regions.push({
        region: key,
        name: region.name,
        elo: Math.round(weighted / totalN),
        // floor, not round: recency-weighted counts are fractional, and a
        // 9.6 rounded up printed "10/10 to unlock" on a region the tier
        // check still considered locked.
        n: Math.floor(totalN),
        tier: confidenceTier(totalN, 10, 30),
      });
    }
  }

  const visible = regions.filter(r => r.tier !== 'HIDDEN').map(r => r.elo);
  const globalElo = visible.length > 0 ? Math.round(median(visible)) : null;

  const paceRaw = computePace(events);
  const steadyRaw = computeSteadiness(events);
  const resRaw = computeResilience(events);
  const nCorrect = events.filter(e => e.correct && !e.timedOut).length;
  const nMisses = events.filter(e => !e.correct).length;

  return {
    regions: regions.sort((a, b) => b.elo - a.elo),
    globalElo,
    pace: paceRaw ? { ...paceRaw, tier: confidenceTier(nCorrect, 8, 25) } : null,
    steadiness: steadyRaw ? { ...steadyRaw, tier: confidenceTier(events.length, 15, 40) } : null,
    resilience: resRaw ? { ...resRaw, tier: confidenceTier(nMisses, 5, 15) } : null,
    totalAnswers: events.length,
  };
}
